import { useMemo, useCallback } from 'react';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import { Resource, Action } from '../../../Constants/socket';

/**
 * Handles contact-level socket events and updates across multiple state locations.
 * Syncs contact updates to selectedConversation and conversations list.
 * 
 * @param {Object} selectedConversation - Currently selected conversation
 * @param {Function} setSelectedConversation - Setter for selected conversation in parent
 * @param {Function} setConversations - Setter for conversations list in parent
 */
const useContactSocketSync = (selectedConversation, setSelectedConversation, setConversations) => {
    // Memoize contact ID for subscription
    const contactIds = useMemo(() =>
        selectedConversation?.contact?.id ? [selectedConversation.contact.id] : []
        , [selectedConversation?.contact?.id]);

    // Subscribe to contact updates
    useSocketSubscription(Resource.CONTACT, contactIds, true);

    // Handle contact updates - memoized to prevent unnecessary socket re-registrations
    const handleContactUpdate = useCallback((data) => {
        if (data.id === selectedConversation?.contact?.id) {
            // Update the selected conversation in the parent
            if (setSelectedConversation) {
                setSelectedConversation(current => ({
                    ...current,
                    contact: {
                        ...current?.contact,
                        ...data
                    }
                }));
            }
        }

        // Update all conversations in the list with this contact
        if (setConversations) {
            setConversations(prevConversations =>
                prevConversations.map(conversation =>
                    conversation.contact?.id === data.id
                        ? {
                            ...conversation,
                            contact: {
                                ...conversation.contact,
                                ...data
                            }
                        }
                        : conversation
                )
            );
        }
    }, [selectedConversation?.contact?.id, setSelectedConversation, setConversations]);

    useSocketEvent(Resource.CONTACT, Action.UPDATE, handleContactUpdate);
};

export default useContactSocketSync;
